import { type ApiClient, createApiClient, shouldRetryQuery } from '@saay/api-client';
import type { AuthController } from '@saay/auth-web';
import { QueryClient } from '@tanstack/react-query';

export interface AppRuntime {
  readonly apiClient: ApiClient;
  readonly auth: AuthController;
  readonly queryClient: QueryClient;
}

export interface AppRuntimeOptions {
  readonly apiBaseUrl: string;
  readonly auth: AuthController;
  /** buyer-web only: the `X-Device-ID` value (AC §7.4). */
  readonly deviceIdProvider?: () => string | Promise<string>;
  readonly fetch?: typeof fetch;
}

/**
 * Server state lives in memory only (FTD §12): no query persister is ever installed, every query
 * revalidates on focus and on screen entry, and the whole cache is cleared on logout or session end.
 */
export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      mutations: {
        // Mutations are never retried by the cache layer: the api-client owns safe retries with
        // the same idempotency key, and unknown results go to "Check status" (FTD §14).
        retry: false,
      },
      queries: {
        gcTime: 60_000,
        refetchOnMount: 'always',
        refetchOnReconnect: true,
        refetchOnWindowFocus: 'always',
        retry: (failureCount, error) => shouldRetryQuery(failureCount, error),
        staleTime: 0,
      },
    },
  });
}

/** Wires the API client to the in-memory tokens and the cache to the session lifecycle. */
export function createAppRuntime(options: AppRuntimeOptions): AppRuntime {
  const { auth } = options;
  const queryClient = createAppQueryClient();
  const apiClient = createApiClient({
    baseUrl: options.apiBaseUrl,
    onSessionExpired: () => auth.markSessionExpired(),
    tokenProvider: {
      getAccessToken: () => auth.getAccessToken(),
      refreshAccessToken: () => auth.refreshAccessToken(),
    },
    ...(options.deviceIdProvider === undefined
      ? {}
      : { deviceIdProvider: options.deviceIdProvider }),
    ...(options.fetch === undefined ? {} : { fetch: options.fetch }),
  });

  auth.onSignOut(() => {
    queryClient.clear();
  });

  return { apiClient, auth, queryClient };
}
